/**
 * Client helper for `GET /api/auth/session`.
 *
 * The server answers 503 with `Retry-After` when it could not verify the
 * session because of a transient failure (AUD-06). That is not "logged out":
 * retry a couple of times before giving up, and never clear client state on it.
 */

import { getCorrelationId } from '@/lib/logging';
import type { DecisionJSON } from '@/lib/access/types';

export const SESSION_ENDPOINT = '/api/auth/session';

/** `reason` values the server sends with `authenticated: false`. */
export type SessionEndedReason =
  | 'offboarded'
  | 'expired'
  | 'revoked'
  | 'invalid'
  | 'user-disabled'
  | 'user-deleted'
  | 'no-session';

export interface SessionStatusResponse {
  authenticated?: boolean;
  user?: unknown | null;
  reason?: SessionEndedReason | string | { kind?: string };
  accessState?: string;
  v?: number;
  uid?: string | null;
  email?: string | null;
  emailKey?: string | null;
  existence?: DecisionJSON['existence'];
  roles?: DecisionJSON['roles'];
  rules?: DecisionJSON['rules'];
  issuedAt?: number;
  fallbackHit?: boolean;
  expiresSoon?: boolean;
}

export function decisionJsonFromSession(data: SessionStatusResponse): DecisionJSON | null {
  if (data.v === 1 && data.reason && typeof data.reason === 'object' && data.reason.kind) {
    return {
      v: 1,
      uid: data.uid ?? null,
      email: data.email ?? null,
      emailKey: data.emailKey ?? null,
      existence: data.existence ?? (data.uid ? 'existing' : 'none'),
      reason: { kind: data.reason.kind as DecisionJSON['reason']['kind'] },
      roles: data.roles ?? [],
      rules: data.rules ?? [],
      issuedAt: data.issuedAt ?? Date.now(),
      fallbackHit: data.fallbackHit,
    };
  }
  return null;
}

/** Normalize Decision JSON or legacy session payloads for AuthContext. */
export function normalizeSessionResponse(data: SessionStatusResponse): {
  authenticated: boolean;
  user: SessionStatusResponse['user'];
  endedReason?: string;
  accessState?: string;
  decisionJson: DecisionJSON | null;
} {
  const kind = typeof data.reason === 'object' && data.reason ? data.reason.kind : undefined;
  const authenticated = data.authenticated ?? kind === 'ok';
  const user =
    data.user ??
    (authenticated
      ? {
          userId: data.uid,
          uid: data.uid,
          email: data.email,
          roles: data.roles ?? [],
        }
      : null);
  const endedReason =
    typeof data.reason === 'string'
      ? data.reason
      : kind === 'invalid-credential'
        ? 'invalid'
        : kind === 'disabled'
          ? 'user-disabled'
          : kind;
  return {
    authenticated: !!authenticated,
    user,
    endedReason,
    accessState: data.accessState,
    decisionJson: decisionJsonFromSession(data),
  };
}

const DEFAULT_RETRIES = 2;
const MAX_RETRY_DELAY_MS = 5_000;
const DEFAULT_RETRY_DELAY_MS = 1_000;

export function isSessionUnavailableStatus(status: number): boolean {
  return status === 503;
}

/** 401 from GET /session: no usable session cookie. Signed-out, not an authorization failure. */
export function isMissingSessionStatus(status: number): boolean {
  return status === 401;
}

function retryDelayMs(response: Response, attempt: number): number {
  const header = Number(response.headers.get('Retry-After'));
  const seconds = Number.isFinite(header) && header > 0 ? header : null;
  const base = seconds ? seconds * 1000 : DEFAULT_RETRY_DELAY_MS * (attempt + 1);
  return Math.min(base, MAX_RETRY_DELAY_MS);
}

export interface FetchSessionOptions {
  retries?: number;
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Fetches the session status, retrying on 503 (`session_verification_unavailable`).
 * Network errors are rethrown after the last attempt.
 */
export async function fetchSessionStatus(options: FetchSessionOptions = {}): Promise<Response> {
  const retries = options.retries ?? DEFAULT_RETRIES;
  const fetchImpl = options.fetchImpl ?? fetch;
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  let response: Response | null = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    response = await fetchImpl(SESSION_ENDPOINT, {
      credentials: 'include',
      headers: { 'X-Correlation-Id': getCorrelationId() },
    });
    if (!isSessionUnavailableStatus(response.status) || attempt === retries) {
      return response;
    }
    await sleep(retryDelayMs(response, attempt));
  }
  return response as Response;
}

/**
 * Copy shown when a previously authenticated user's session is reported as
 * ended. Only an explicit `offboarded` reason may claim an administrator acted.
 */
export function describeSessionEnded(reason: string | undefined): { title: string; description: string; adminAction: boolean } {
  if (reason === 'offboarded' || reason === 'disabled' || reason === 'user-disabled') {
    return { title: 'Access removed', description: 'An administrator removed your access.', adminAction: true };
  }
  if (reason === 'user-deleted') {
    return { title: 'Account removed', description: 'Your account no longer exists. Please contact your administrator.', adminAction: true };
  }
  return { title: 'Session ended', description: 'Your session has ended. Please sign in again.', adminAction: false };
}
